import { useState } from "react";
import { useParams } from "react-router";
import { format } from "date-fns";
import type { DuviSharedAdminStatusPillStatus } from "@duvi-agency/ui";
import { users } from "../../api/client";
import {
  useDuviCoreAdminQuery,
  useDuviCoreAdminMutation,
} from "../../hooks/use-api";

export function duviCoreAdminFormatDate(dateStr?: string): string {
  if (!dateStr) return "—";
  try {
    return format(new Date(dateStr), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
}

// NOTE: the detail page's status map differs from the list page — it maps
// FORCE_CHANGE_PASSWORD → active and omits invited/password_reset. Preserved
// verbatim (behaviour contract).
export function duviCoreAdminMapDetailStatus(status: string): string {
  switch (status) {
    case "CONFIRMED":
    case "FORCE_CHANGE_PASSWORD":
      return "active";
    case "disabled":
    case "DISABLED":
      return "disabled";
    case "archived":
      return "archived";
    default:
      return status.toLowerCase();
  }
}

export function duviCoreAdminDetailStatusTone(
  status: string,
): DuviSharedAdminStatusPillStatus {
  switch (status) {
    case "active":
      return "success";
    case "disabled":
      return "error";
    case "archived":
      return "inactive";
    default:
      return "info";
  }
}

export interface DuviCoreAdminUserDetailModel {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  status: string;
  createdAt: string;
  mfaEnabled: boolean;
}

export function useDuviCoreAdminUserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const { data, isLoading, refetch } = useDuviCoreAdminQuery(
    () => users.get(userId!),
    [userId],
    { enabled: !!userId },
  );

  const forceResetMutation = useDuviCoreAdminMutation(() =>
    users.forceResetPassword(userId!),
  );
  const disableMutation = useDuviCoreAdminMutation(() =>
    users.disable(userId!),
  );
  const enableMutation = useDuviCoreAdminMutation(() => users.enable(userId!));
  const archiveMutation = useDuviCoreAdminMutation(() =>
    users.archive(userId!),
  );

  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const user: DuviCoreAdminUserDetailModel | undefined = data
    ? {
        id: data.user_id,
        name: data.name,
        email: data.email,
        avatarUrl: data.avatar_url,
        status: duviCoreAdminMapDetailStatus(data.status),
        createdAt: duviCoreAdminFormatDate(data.created_at),
        mfaEnabled: data.mfa_enabled ?? false,
      }
    : undefined;

  const handleForceReset = () => {
    void forceResetMutation.mutate(undefined as never);
  };
  const handleDisable = async () => {
    await disableMutation.mutate(undefined as never);
    refetch();
  };
  const handleEnable = async () => {
    await enableMutation.mutate(undefined as never);
    refetch();
  };
  const handleArchive = async () => {
    await archiveMutation.mutate(undefined as never);
    setShowArchiveModal(false);
    refetch();
  };

  return {
    user,
    isLoading,
    showArchiveModal,
    setShowArchiveModal,
    forceResetLoading: forceResetMutation.isLoading,
    disableLoading: disableMutation.isLoading,
    enableLoading: enableMutation.isLoading,
    archiveLoading: archiveMutation.isLoading,
    handleForceReset,
    handleDisable,
    handleEnable,
    handleArchive,
  };
}
